/**
 * F1 · What the kitchen tablet is allowed to say, and how old it is.
 *
 * The screen itself is `routes/Tablet.svelte`. This file holds the two things that
 * are not layout: the refresh loop, and an honest account of what each panel's data
 * source actually is today.
 *
 * ## The rule this file exists to enforce
 *
 * The tablet hangs on a wall and is read at a glance, which means nobody will check
 * it. A wrong number on a phone gets questioned; the same number on a wall becomes
 * what the household believes. So every panel resolves to exactly one of five
 * states, and four of them are refusals — `SourceState` in `lib/sources.ts`, which
 * is where the two types below moved to when F7's week and F8's list turned out to
 * need the same vocabulary.
 *
 * `unwired` is the one that matters most, and it is a compile-time fact rather than
 * a runtime one: it is declared, in `SOURCES` below, with the reason. Most of what
 * F1 asks for has no data source anywhere in the estate today. The temptation is to
 * fill it with something plausible — a placeholder chore list, a forecast from a
 * free API, "everyone is home". Every one of those would be a lie that the wall
 * makes authoritative. They render an em dash and say what would fill them.
 *
 * ## Why the refresh is five minutes
 *
 * `/public/glance` caches for five minutes at the server, deliberately — polling it
 * faster returns the identical body and tells a watcher nothing new. Anything the
 * tablet shows is therefore at best five minutes old, and it says so.
 */
import { api, ApiError } from "./api";
import type { Attention } from "./estate.svelte";
import { glance } from "./glance.svelte";
import type { SourceState, Unwired } from "./sources";
import { session } from "./stores.svelte";

// Both types moved to `sources.ts` when F7 and F8 needed them too. Re-exported so
// every existing `from "../lib/tablet.svelte"` keeps working and the move touched
// no component — the same reasoning as `lib/auth.ts`'s re-export.
export type { SourceState, Unwired };

/** Matches `/public/glance`'s own cache window. Faster is churn, not freshness. */
export const REFRESH_MS = 5 * 60_000;

/**
 * Three missed refreshes before a figure is called stale.
 *
 * One missed sweep is a cold Cloud Run start and happens routinely; calling that
 * stale would light an amber chip most mornings and train everyone to ignore it.
 * Three in a row is not a blip.
 */
export const STALE_MS = 3 * REFRESH_MS + 60_000;

/**
 * The two panels F1 asks for that nothing in the estate can answer today.
 *
 * Each was checked against `fabric-api`'s routes rather than assumed:
 *
 *  · **Who is where** — HA has device trackers, but Fabric serves no presence route,
 *    and `/public/glance` excludes every occupancy signal on purpose (its allowlist
 *    comment: *would this tell a stranger whether the house is empty?*). There is
 *    nothing to read, and the thing that would be read is deliberately withheld.
 *  · **The boys' chores** — F6 owns the ledger and it is not built. Homestead has no
 *    chores collection.
 *
 * **Tonight** used to be the third. It is gone from this map because F7 is now
 * built, and F7's week is where tonight belongs — a card that answered only
 * "tonight" beside a card that draws seven days would be the same absent calendar
 * explained twice. The calendar's own missing-source note moved with it, to
 * `lib/week.svelte.ts`.
 */
export const SOURCES: Record<"presence" | "chores", Unwired> = {
  presence: {
    title: "Who is where",
    // Kept to two short sentences, like the other. This is a wall: the reasoning
    // belongs in the comment above, not on the screen, and a paragraph nobody reads
    // is the same as no explanation at all.
    why: "Nothing publishes presence here. Fabric serves no presence route.",
    next: "Add one to fabric-api and this fills itself.",
  },
  chores: {
    title: "The boys",
    why: "There is no chore ledger yet. That is F6, and Homestead will own it.",
    next: "Build F6. The balance belongs here, so it is never a dispute.",
  },
};

/**
 * The attention feed, read as the tablet.
 *
 * This is the one panel that is wired and still usually cannot answer, and the
 * distinction is worth the code: `GET /attention` requires **partner** or above, and
 * the tablet runs as `display`. A 403 here means *this screen may not know*, which
 * must never render as *nothing wants you* — the second is a claim about the house
 * and it would be false on precisely the days it mattered.
 */
class Tablet {
  attention = $state<Attention[]>([]);
  attentionState = $state<SourceState>("loading");
  attentionError = $state<string | null>(null);
  /** When the attention feed last answered. Null until it has. */
  attentionAt = $state<number | null>(null);

  /**
   * Ticks so every "as of" and every staleness chip re-evaluates without anything
   * having to invalidate them. A wall clock that stops at the minute the page
   * loaded is worse than no clock.
   */
  now = $state(Date.now());

  private timers: ReturnType<typeof setInterval>[] = [];

  /** True once nobody has been signed in on this tablet — it has never been paired. */
  get unpaired(): boolean {
    return session.ready && !session.claims;
  }

  /** Age of the oldest thing on screen, or null when nothing has ever loaded. */
  get oldestReadAt(): number | null {
    const ages = [glance.readAt, this.attentionAt].filter((v): v is number => v !== null);
    return ages.length ? Math.min(...ages) : null;
  }

  isStale(at: number | null): boolean {
    return at === null || this.now - at > STALE_MS;
  }

  async refresh(): Promise<void> {
    await glance.load();

    // No session at all: do not ask. An unauthenticated call would 401 and be
    // recorded as a failure, when the truth is that this tablet was never paired.
    if (!session.claims) {
      this.attentionState = "denied";
      this.attentionError = null;
      return;
    }

    try {
      const r = await api.get<{ items: Attention[] }>("/attention");
      this.attention = r.items;
      this.attentionState = "ready";
      this.attentionAt = Date.now();
      this.attentionError = null;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 401)) {
        this.attentionState = "denied";
        this.attentionError = null;
      } else {
        this.attentionState = "failed";
        this.attentionError = e instanceof ApiError ? e.message : "Could not reach Fabric";
      }
    }
  }

  /**
   * Start the loop. Returns a teardown, so the component owns the lifetime rather
   * than this module leaking an interval into every other route.
   */
  start(): () => void {
    void this.refresh();
    this.timers.push(setInterval(() => void this.refresh(), REFRESH_MS));
    // 15 seconds, not 1000ms: the clock shows minutes, and a per-second timer on a
    // screen that is on all day is a wakeup every second for no visible change.
    this.timers.push(setInterval(() => (this.now = Date.now()), 15_000));

    // A kiosk tablet sleeps. Coming back from sleep with a two-hour-old screen and
    // waiting five minutes for the first sweep is the worst version of this: it
    // looks live and is not.
    const onWake = () => {
      this.now = Date.now();
      if (document.visibilityState === "visible") void this.refresh();
    };
    document.addEventListener("visibilitychange", onWake);
    addEventListener("online", onWake);

    return () => {
      for (const t of this.timers) clearInterval(t);
      this.timers = [];
      document.removeEventListener("visibilitychange", onWake);
      removeEventListener("online", onWake);
    };
  }
}

export const tablet = new Tablet();

/** "06:42" in the household's timezone, whatever the device thinks. */
export function clock(at: number): string {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(at);
}

/** "Saturday, 15 August" — the date a person would say out loud. */
export function longDate(at: number): string {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(at);
}
